<script setup>
import { ref, computed, watch } from 'vue'
import { COUNTRIES, parsePhone, composePhone, findByCode } from '@/composables/useCountryCodes.js'

const props = defineProps({
  modelValue:  { type: String, default: '' },
  placeholder: { type: String, default: '' },
  required:    { type: Boolean, default: false },
  disabled:    { type: Boolean, default: false },
  error:       { type: String, default: '' },
  id:          { type: String, default: '' },
})
const emit = defineEmits([ 'update:modelValue' ])

const initial    = parsePhone(props.modelValue)
const countryCode = ref(initial.country.code)
const national    = ref(initial.national)

const country = computed(() => findByCode(countryCode.value))

// Track whether the next modelValue change comes from our own emit. When
// the user picks (say) US from the dropdown we emit "+1 …", and parsing
// "+1" back picks whichever +1-country appears first in COUNTRIES — that
// would silently revert the user's choice for shared dial codes (US/CA,
// RU/KZ). Suppress the re-parse on self-emit; only adopt external rewrites
// of modelValue.
let suppressParse = false

const emitComposed = () => {
  suppressParse = true
  emit('update:modelValue', composePhone(country.value, national.value))
}

watch(countryCode, emitComposed)
watch(national, emitComposed)

watch(() => props.modelValue, (v) => {
  if (suppressParse) { suppressParse = false; return }
  const parsed = parsePhone(v)
  if (parsed.country.code !== countryCode.value) countryCode.value = parsed.country.code
  if (parsed.national     !== national.value)    national.value    = parsed.national
})
</script>

<template>
  <div class="flex items-stretch gap-2">
    <div class="relative">
      <select
        v-model="countryCode"
        :disabled="disabled"
        :aria-label="'Country code'"
        class="h-full pl-2 pr-7 text-sm rounded-[var(--radius-input)] border border-[color:var(--line)] bg-[color:var(--surface)] text-[color:var(--ink)] appearance-none focus:outline-2 focus:outline-[color:var(--accent)]"
      >
        <option v-for="c in COUNTRIES" :key="c.code" :value="c.code">
          {{ c.flag }} {{ c.dial }}
        </option>
      </select>
      <span
        aria-hidden="true"
        class="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-[color:var(--muted)] text-xs"
      >▾</span>
    </div>
    <input
      :id="id"
      v-model="national"
      type="tel"
      inputmode="tel"
      autocomplete="tel-national"
      :placeholder="placeholder"
      :required="required"
      :disabled="disabled"
      :aria-invalid="error ? 'true' : undefined"
      class="flex-1 w-full px-3 py-2 rounded-[var(--radius-input)] border bg-[color:var(--surface)] text-[color:var(--ink)] text-sm focus:outline-2 focus:outline-[color:var(--accent)]"
      :class="error ? 'border-[color:var(--danger)]' : 'border-[color:var(--line)]'"
    >
  </div>
  <p v-if="error" class="mt-1 text-xs text-[color:var(--danger)]">{{ error }}</p>
</template>
